import { useEffect, useMemo, useState } from "react";
import { FileText, Save, Eye, Send, EyeOff, ChevronLeft, Plus, Trash2 } from "lucide-react";
import client, { extractError } from "@/api";

const PAGE_LABELS = {
  home: "Home",
  pricing: "Pricing",
  terms: "Terms & Conditions",
  privacy: "Privacy Policy",
  refund: "Refund / Cancellation",
  shipping: "Shipping / Delivery",
  contact: "Contact Us",
  grievance: "Grievance",
};

/** HOME page has structured (JSON-per-field) content; the other pages have a
 * single body_html string plus, for pricing, an intro + footer_notes_html. */
const HOME_FIELDS = [
  // ------ HERO ------
  { key: "hero_eyebrow",            label: "Hero eyebrow (small caps line)", type: "text", section: "Hero" },
  { key: "hero_headline",           label: "Hero headline",              type: "text",     section: "Hero" },
  { key: "hero_supporting",         label: "Hero supporting text",       type: "textarea", section: "Hero" },
  { key: "primary_cta_text",        label: "Primary CTA text",           type: "text",     section: "Hero" },
  { key: "primary_cta_href",        label: "Primary CTA destination",    type: "text",     section: "Hero" },
  { key: "secondary_cta_text",      label: "Secondary CTA text",         type: "text",     section: "Hero" },
  { key: "secondary_cta_href",      label: "Secondary CTA destination",  type: "text",     section: "Hero" },

  // ------ TRUST STRIP ------
  { key: "trust_strip_visible",     label: "Show trust strip",           type: "boolean",  section: "Trust strip" },
  { key: "trust_strip_items",       label: "Trust strip items (4 recommended)", type: "list-title-desc", section: "Trust strip" },

  // ------ HOW IT WORKS ------
  { key: "how_it_works_visible",    label: "Show How-It-Works section",  type: "boolean",  section: "How It Works" },
  { key: "how_it_works_heading",    label: "How It Works heading",       type: "text",     section: "How It Works" },
  { key: "how_it_works_supporting", label: "How It Works supporting text",type: "textarea", section: "How It Works" },
  { key: "how_it_works_steps",      label: "Steps (4 recommended)",      type: "list-title-desc", section: "How It Works" },

  // ------ BENEFITS ------
  { key: "benefits_visible",        label: "Show Benefits section",      type: "boolean",  section: "Benefits" },
  { key: "benefits_heading",        label: "Benefits heading",           type: "text",     section: "Benefits" },
  { key: "benefits_supporting",     label: "Benefits supporting text",   type: "textarea", section: "Benefits" },
  { key: "benefits_items",          label: "Benefits items (4 recommended)", type: "list-title-desc", section: "Benefits" },

  // ------ DIRECT + CIRCULAR ------
  { key: "barter_types_visible",    label: "Show Direct/Circular section", type: "boolean", section: "Direct & Circular Barter" },
  { key: "barter_types_heading",    label: "Section heading",            type: "text",     section: "Direct & Circular Barter" },
  { key: "barter_types_supporting", label: "Section supporting text",    type: "textarea", section: "Direct & Circular Barter" },
  { key: "direct_barter_title",     label: "Direct barter title",        type: "text",     section: "Direct & Circular Barter" },
  { key: "direct_barter_description", label: "Direct barter description", type: "textarea", section: "Direct & Circular Barter" },
  { key: "circular_barter_title",   label: "Circular barter title",      type: "text",     section: "Direct & Circular Barter" },
  { key: "circular_barter_description", label: "Circular barter description", type: "textarea", section: "Direct & Circular Barter" },

  // ------ MARKETPLACE PREVIEW ------
  { key: "marketplace_preview_visible",    label: "Show Marketplace preview", type: "boolean", section: "Marketplace preview" },
  { key: "marketplace_preview_heading",    label: "Section heading",     type: "text",     section: "Marketplace preview" },
  { key: "marketplace_preview_supporting", label: "Section supporting text", type: "textarea", section: "Marketplace preview" },
  { key: "marketplace_preview_cta",        label: "CTA label",           type: "text",     section: "Marketplace preview" },

  // ------ TRUST SECTION ------
  { key: "trust_section_visible",   label: "Show trust section",         type: "boolean",  section: "Trust section" },
  { key: "trust_section_heading",   label: "Trust section heading",      type: "text",     section: "Trust section" },
  { key: "trust_section_supporting",label: "Trust section supporting text", type: "textarea", section: "Trust section" },
  { key: "trust_section_points",    label: "Trust points (4 recommended)", type: "list-title-desc", section: "Trust section" },

  // ------ FINAL CTA ------
  { key: "final_cta_heading",       label: "Final CTA heading",          type: "text",     section: "Final CTA" },
  { key: "final_cta_description",   label: "Final CTA description",      type: "textarea", section: "Final CTA" },
  { key: "final_cta_primary_text",  label: "Primary CTA text",           type: "text",     section: "Final CTA" },
  { key: "final_cta_primary_href",  label: "Primary CTA destination",    type: "text",     section: "Final CTA" },
  { key: "final_cta_secondary_text",label: "Secondary CTA text",         type: "text",     section: "Final CTA" },
  { key: "final_cta_secondary_href",label: "Secondary CTA destination",  type: "text",     section: "Final CTA" },
];

const PRICING_FIELDS = [
  { key: "intro",             label: "Introductory paragraph",   type: "textarea"  },
  { key: "footer_notes_html", label: "Descriptive HTML (below plan grid)", type: "html" },
];

const LEGAL_FIELDS = [
  { key: "body_html", label: "Page body (HTML)", type: "html" },
];

function schemaFor(pageKey) {
  if (pageKey === "home") return HOME_FIELDS;
  if (pageKey === "pricing") return PRICING_FIELDS;
  return LEGAL_FIELDS;
}

/* ================================================================ LIST */
export default function AdminWebsiteContentPage({ user }) {
  const canEdit = user?.role === "SUPER_ADMIN";
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    return client.get("/admin/website-content")
      .then((r) => setRows(r.data))
      .catch((err) => setError(extractError(err)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  if (selected) {
    return <ContentEditor
      pageKey={selected}
      canEdit={canEdit}
      onBack={() => { setSelected(null); load(); }}
    />;
  }

  return (
    <div className="admin-page" data-testid="admin-website-content-page">
      <header className="page-header">
        <div>
          <h2><FileText size={18} /> Website Content</h2>
          <p className="muted">Manage the content of the 8 public pages. Edits are saved as drafts and only become visible after Publish.</p>
        </div>
      </header>
      {error && <div className="form-error">{error}</div>}
      {loading ? <div className="loading-block">Loading…</div> : (
        <table className="admin-table" data-testid="website-content-table">
          <thead>
            <tr>
              <th>Page</th>
              <th>Status</th>
              <th>Last updated</th>
              <th>Updated by</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.page_key} data-testid={`row-${row.page_key}`}>
                <td>
                  <strong>{PAGE_LABELS[row.page_key] || row.page_key}</strong><br />
                  <span className="muted small">/{row.page_key === "home" ? "" : row.page_key}</span>
                </td>
                <td>
                  {row.is_published
                    ? <span className="chip status-verified" data-testid={`status-${row.page_key}-published`}>Published</span>
                    : <span className="chip status-cancelled" data-testid={`status-${row.page_key}-unpublished`}>Unpublished</span>}
                  {row.has_draft && <span className="chip" data-testid={`status-${row.page_key}-draft`} style={{ marginLeft: 6 }}>Draft pending</span>}
                </td>
                <td>{row.updated_at ? new Date(row.updated_at).toLocaleString() : "—"}</td>
                <td>{row.updated_by || <span className="muted small">—</span>}</td>
                <td>
                  <button type="button" className="ghost-button" data-testid={`open-${row.page_key}`} onClick={() => setSelected(row.page_key)}>
                    {canEdit ? "Edit" : "View"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ============================================================== EDITOR */
function ContentEditor({ pageKey, canEdit, onBack }) {
  const schema = useMemo(() => schemaFor(pageKey), [pageKey]);
  const [row, setRow] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishedSnap, setPublishedSnap] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [draft, setDraft] = useState({});
  const [title, setTitle] = useState("");
  const [toast, setToast] = useState("");

  useEffect(() => {
    client.get(`/admin/website-content/${pageKey}`).then((r) => {
      setRow(r.data);
      setPublishedSnap(r.data.content || {});
      const baseline = r.data.draft_content || r.data.content || {};
      setDraft(baseline);
      setTitle(r.data.title || "");
    }).catch((err) => setError(extractError(err)));
  }, [pageKey]);

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const saveDraft = async () => {
    if (!canEdit) return;
    setSaving(true);
    setError("");
    try {
      const r = await client.put(`/admin/website-content/${pageKey}`, { title, content: draft });
      setRow(r.data);
      setToast("Draft saved. Not yet visible publicly.");
      setTimeout(() => setToast(""), 3200);
    } catch (err) { setError(extractError(err)); }
    finally { setSaving(false); }
  };

  const publish = async () => {
    if (!canEdit) return;
    setPublishing(true);
    setError("");
    try {
      const r = await client.post(`/admin/website-content/${pageKey}/publish`);
      setRow(r.data);
      setPublishedSnap(r.data.content || {});
      setToast("Published. Live on the public website.");
      setTimeout(() => setToast(""), 3200);
    } catch (err) { setError(extractError(err)); }
    finally { setPublishing(false); }
  };

  const unpublish = async () => {
    if (!canEdit) return;
    setError("");
    try {
      const r = await client.post(`/admin/website-content/${pageKey}/unpublish`);
      setRow(r.data);
      setToast("Page is now hidden from public visitors.");
      setTimeout(() => setToast(""), 3200);
    } catch (err) { setError(extractError(err)); }
  };

  if (!row) return <div className="loading-block">Loading…</div>;

  return (
    <div className="admin-page" data-testid="website-content-editor">
      <button type="button" className="link-button back" onClick={onBack}><ChevronLeft size={14} /> Back to Website Content</button>
      <header className="page-header">
        <div>
          <p className="eyebrow">WEBSITE CONTENT</p>
          <h2>{PAGE_LABELS[pageKey]}</h2>
          <p className="muted">Draft saves are private. Only Publish makes changes visible to visitors.</p>
        </div>
        <div className="chip-stack">
          {row.is_published
            ? <span className="chip status-verified" data-testid="status-published">Published</span>
            : <span className="chip status-cancelled" data-testid="status-unpublished">Unpublished</span>}
          {row.has_draft && <span className="chip" data-testid="status-draft-pending">Draft pending</span>}
          <button type="button" className="ghost-button" onClick={() => setPreviewMode(!previewMode)} data-testid="toggle-preview-btn">
            <Eye size={13} /> {previewMode ? "Back to editor" : "Preview draft"}
          </button>
          {canEdit && (
            <>
              <button type="button" className="ghost-button" onClick={saveDraft} disabled={saving} data-testid="save-draft-btn"><Save size={13} /> {saving ? "Saving…" : "Save draft"}</button>
              <button type="button" className="primary-button" onClick={publish} disabled={publishing || !row.has_draft} data-testid="publish-btn"><Send size={13} /> {publishing ? "Publishing…" : "Publish"}</button>
              <button type="button" className="danger-button" onClick={unpublish} data-testid="unpublish-btn"><EyeOff size={13} /> Unpublish</button>
            </>
          )}
        </div>
      </header>

      {toast && <div className="form-success" data-testid="editor-toast">{toast}</div>}
      {error && <div className="form-error" data-testid="editor-error">{error}</div>}

      {!canEdit && <div className="form-note">You are viewing this page in read-only mode. Only SUPER_ADMIN can edit or publish content.</div>}

      {previewMode ? (
        <section className="section-block" data-testid="preview-section">
          <p className="eyebrow">DRAFT PREVIEW</p>
          <PreviewPane pageKey={pageKey} content={draft} title={title} />
        </section>
      ) : (
        <>
          <section className="section-block">
            <label>Page title</label>
            <input value={title} disabled={!canEdit} onChange={(e) => setTitle(e.target.value)} data-testid="edit-title" />
          </section>

          {schema.map((f, idx) => {
            const prev = idx > 0 ? schema[idx - 1] : null;
            const showSectionHeading = f.section && (!prev || prev.section !== f.section);
            return (
              <div key={f.key}>
                {showSectionHeading && (
                  <div className="section-block" style={{ background: "#f0f4ee", padding: "10px 14px", marginBottom: 0, borderLeft: "3px solid var(--deep)" }} data-testid={`section-heading-${f.section.replace(/\s+/g, "-").toLowerCase()}`}>
                    <p className="eyebrow" style={{ margin: 0, letterSpacing: 2 }}>{f.section}</p>
                  </div>
                )}
                <FieldEditor
                  field={f}
                  value={draft[f.key]}
                  onChange={(v) => setField(f.key, v)}
                  disabled={!canEdit}
                  publishedValue={publishedSnap?.[f.key]}
                />
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

/* ============================================================== FIELDS */
function FieldEditor({ field, value, onChange, disabled, publishedValue }) {
  const testid = `field-${field.key}`;
  if (field.type === "text") {
    return (
      <section className="section-block">
        <label>{field.label}</label>
        <input value={value || ""} disabled={disabled} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      </section>
    );
  }
  if (field.type === "textarea") {
    return (
      <section className="section-block">
        <label>{field.label}</label>
        <textarea rows={4} value={value || ""} disabled={disabled} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      </section>
    );
  }
  if (field.type === "html") {
    return (
      <section className="section-block">
        <label>{field.label} <span className="muted small">(HTML — inline styling and links allowed; scripts stripped on render)</span></label>
        <textarea rows={12} value={value || ""} disabled={disabled} onChange={(e) => onChange(e.target.value)} data-testid={testid} spellCheck={false} style={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 13 }} />
      </section>
    );
  }
  if (field.type === "list-title-desc") {
    const list = Array.isArray(value) ? value : [];
    const setAt = (i, patch) => onChange(list.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
    const add = () => onChange([...list, { title: "", description: "" }]);
    const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
    return (
      <section className="section-block">
        <div className="section-heading">
          <div><label>{field.label}</label><p className="muted small">Ordered list of title + description entries.</p></div>
          {!disabled && <button type="button" className="ghost-button" onClick={add} data-testid={`${testid}-add`}><Plus size={13} /> Add</button>}
        </div>
        <ul className="finance-list">
          {list.length === 0 && <li className="muted small">No entries yet.</li>}
          {list.map((row, i) => (
            <li key={i} data-testid={`${testid}-item-${i}`} style={{ flexDirection: "column", alignItems: "stretch", gap: 6 }}>
              <input value={row.title || ""} disabled={disabled} placeholder="Title" onChange={(e) => setAt(i, { title: e.target.value })} data-testid={`${testid}-item-${i}-title`} />
              <textarea rows={2} value={row.description || ""} disabled={disabled} placeholder="Description" onChange={(e) => setAt(i, { description: e.target.value })} data-testid={`${testid}-item-${i}-desc`} />
              {!disabled && <button type="button" className="danger-button" onClick={() => remove(i)} data-testid={`${testid}-item-${i}-remove`} style={{ alignSelf: "flex-start" }}><Trash2 size={12} /> Remove</button>}
            </li>
          ))}
        </ul>
      </section>
    );
  }
  if (field.type === "boolean") {
    const checked = value !== false;  // default = shown
    return (
      <section className="section-block">
        <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: disabled ? "default" : "pointer" }}>
          <input type="checkbox" checked={checked} disabled={disabled}
            onChange={(e) => onChange(e.target.checked)}
            data-testid={testid}
            style={{ width: 18, height: 18 }} />
          <span>{field.label}</span>
        </label>
        <p className="muted small" style={{ marginTop: 6 }}>
          Content is preserved even when hidden — re-tick to show again.
        </p>
      </section>
    );
  }
  return null;
}

/* ============================================================== PREVIEW */
function PreviewPane({ pageKey, content, title }) {
  if (pageKey === "home") {
    const steps = Array.isArray(content.how_it_works_steps) ? content.how_it_works_steps : [];
    const benefits = Array.isArray(content.benefits_items) ? content.benefits_items : [];
    return (
      <div className="home-page" style={{ background: "transparent" }}>
        <section className="home-hero">
          <p className="eyebrow">BARTER INDIA</p>
          <h1>{content.hero_headline}</h1>
          <p className="home-hero-supporting">{content.hero_supporting}</p>
        </section>
        {steps.length > 0 && (
          <section className="home-section">
            <h2>{content.how_it_works_heading}</h2>
            <div className="home-grid">
              {steps.map((s, i) => (<article key={i} className="home-tile"><div className="home-step-number">{i + 1}</div><h3>{s.title}</h3><p>{s.description}</p></article>))}
            </div>
          </section>
        )}
        {benefits.length > 0 && (
          <section className="home-section">
            <h2>{content.benefits_heading}</h2>
            <div className="home-grid">
              {benefits.map((b, i) => (<article key={i} className="home-tile"><h3>{b.title}</h3><p>{b.description}</p></article>))}
            </div>
          </section>
        )}
        {content.final_cta_heading && (
          <section className="home-final-cta"><h2>{content.final_cta_heading}</h2><p>{content.final_cta_description}</p></section>
        )}
      </div>
    );
  }
  if (pageKey === "pricing") {
    const clean = String(content.footer_notes_html || "")
      .replace(/<script[\s\S]*?<\/script>/gi, "")
      .replace(/\son\w+="[^"]*"/gi, "");
    return (
      <div>
        <h1>{title}</h1>
        <p>{content.intro}</p>
        <p className="muted small">(Live plan grid renders here on the public page.)</p>
        <div dangerouslySetInnerHTML={{ __html: clean }} />
      </div>
    );
  }
  // Legal / info
  const clean = String(content.body_html || "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/\son\w+="[^"]*"/gi, "");
  return (
    <div>
      <h1>{title}</h1>
      <div dangerouslySetInnerHTML={{ __html: clean }} />
    </div>
  );
}
